import {
  FormFieldWidget,
  FormM2MUploadFieldWidget,
  http,
  ModelFieldType,
  SPI,
  ViewType,
  Widget
} from '@kunlun/dependencies';
import { OioMessage } from '@kunlun/vue-ui-antd';
import { stringifyToGraphql } from '../../../../utils/serialize';

/**
 * 品牌分销「我邀请的客户」建档页 —— 营业执照附件上传识别 Widget。
 *
 * <p>覆盖 model deli.aries.seller.DeliAriesSellerDistributor 上的 licenseAttachment
 * （多对多 Upload）字段：用户上传营业执照图片后，取文件 url 调用后端
 * constructBusinessLicense（阿里云 OCR），把识别结果回写到表单对应字段。
 * 后端比对营业执照名称与「客户名称」，不一致时抛错，前端提示且不回写。</p>
 */
@SPI.ClassFactory(
  FormFieldWidget.Token({
    viewType: [ViewType.Form],
    ttype: ModelFieldType.ManyToMany,
    widget: ['Upload'],
    name: 'licenseAttachment',
    model: 'deli.aries.seller.DeliAriesSellerDistributor'
  })
)
export class FormM2MBusinessLicenseUploadFieldWidget extends FormM2MUploadFieldWidget {
  /** 仅在文件数量增加（新上传）时识别，删除文件不触发。 */
  @Widget.Method()
  public change(data) {
    const prevCount = Array.isArray(this.value) ? (this.value as any[]).length : 0;
    super.change(data);
    const list = Array.isArray(data) ? data : [];
    if (list.length > prevCount) {
      this.recognizeBusinessLicense(list);
    }
  }

  private async recognizeBusinessLicense(files: any[]): Promise<void> {
    const url = files.map((f) => f?.url).find((u) => !!u);
    if (!url) {
      return;
    }
    const data = {
      licenseAttachment: [{ url }],
      name: this.formData?.name ?? null
    };
    const gql = `
      query {
        deliAriesSellerDistributorQuery {
          constructBusinessLicense(data: ${stringifyToGraphql(data)}) {
            certificateNumber
            creditCode
            legalPerson
            contactAddressStreet2
            effectiveTimeRule
            licenseStartTime
            licenseEndTime
            contactAddress {
              countryCode
              province
              provinceCode
              provinceName
              city
              cityCode
              cityName
              district
              districtCode
              districtName
              street2
              fullAddress
            }
          }
        }
      }
    `;
    try {
      const res: any = await http.query(this.model.moduleName, gql);
      const result = res?.data?.deliAriesSellerDistributorQuery?.constructBusinessLicense;
      if (!result) {
        return;
      }
      this.fillBackByLicense(result);
    } catch (e: any) {
      OioMessage.error(e?.message || '营业执照识别失败，请核实后重试');
    }
  }

  /** 仅回写识别出的非空字段，避免覆盖用户已填内容。 */
  private fillBackByLicense(result: Record<string, any>): void {
    const simpleFields = [
      'certificateNumber',
      'creditCode',
      'legalPerson',
      'contactAddressStreet2',
      'effectiveTimeRule',
      'licenseStartTime',
      'licenseEndTime'
    ];
    simpleFields.forEach((key) => {
      const val = result[key];
      if (val !== null && val !== undefined && val !== '') {
        this.formData[key] = val;
      }
    });
    if (result.contactAddress) {
      this.formData.contactAddress = result.contactAddress;
    }
    this.reloadFormData$.subject.next(true);
    OioMessage.success('营业执照信息已识别');
  }
}
